import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import ReservationListItem from '@/components/TicketListItem';

/**
 * ── /components/ticket-list-item ─────────────────────────────────────────
 *
 * TicketListItem (imported as `ReservationListItem`) — one row in a user's
 * bookings list. Three real call sites, all mapping over a fetched list:
 *   - profile/index.tsx's "Upcoming bookings" section — `onPress` pushes to
 *     `/bookings/[id]`, no `past`
 *   - profile/index.tsx's "Past bookings" section — same `onPress` shape,
 *     plus `past`
 *   - profile/index.tsx's `ApprovalPendingSection` — `onPress` pushes to
 *     `/requests/[id]`, no `past`
 *   - delete-account/index.tsx's upcoming-bookings list — NO `onPress` (the
 *     row is inert there, just a receipt of what you'd lose)
 * `imageUrl` is optional in all four: it's `event.images[0]?.url`/
 * `.at(0)?.url`, an event can have zero images uploaded, and the fixture
 * below for that case is real, not synthetic.
 *
 * Covers, in order: the tappable row (live, `onPress` — most real call
 * sites pass one, but the old all.tsx holding-pen preview never exercised
 * it), the inert row (delete-account's shape), the 1-line location
 * truncation, the 3-line title clamp, the missing-image fallback, and
 * `past`'s per-section dim.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function TicketListItemPreview() {
  // Drives the live demo — same tap-to-confirm pattern as ps-button.tsx's
  // live instance. Real screens `router.push` on tap; this preview has
  // nowhere to push to, so it swaps a line of copy instead, just to prove
  // the row is actually wired to `onPress` and not just showing the pointer
  // cursor.
  const [tapped, setTapped] = useState(false);

  return (
    <>
      <Head>
        <title>TicketListItem · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Live — tappable, the profile page's shape (both bookings
            sections and ApprovalPendingSection all pass onPress). Tap to
            confirm the whole card is the hit target (`cursor="pointer"` +
            `onPress` sit on the OUTER YStack in the component, not just the
            location row), same as the real card pushing to
            /bookings/[id] or /requests/[id]. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap the row (profile&apos;s bookings/pending-request rows,
              which push to a detail screen on tap)
            </Text>
            <ReservationListItem
              imageUrl="https://picsum.photos/id/1015/600/800"
              title="Pottery Workshop"
              slotDatetime="2026-08-20T11:00:00"
              ticketCount={1}
              venueName="Versova, Mumbai"
              googleMapsUrl="https://maps.google.com/?q=Versova,Mumbai"
              onPress={() => setTapped((t) => !t)}
            />
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              {tapped ? 'tapped — onPress fired' : 'not tapped yet'}
            </Text>
          </YStack>

          {/* Inert — no onPress. delete-account's UpcomingBookings list
            passes no handler at all (the row is a receipt of what you'd
            lose, not a link anywhere), so cursor/onPress on the component's
            outer YStack both resolve to undefined and the row doesn't
            react to a tap. Singular "1 ticket" also lives here — the
            ticketCount === 1 branch, vs the plural fixtures below. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              inert — no onPress (delete-account&apos;s bookings-you&apos;d-lose
              list); &quot;1 ticket&quot; singular
            </Text>
            <ReservationListItem
              imageUrl="https://picsum.photos/id/1015/600/800"
              title="Pottery Workshop"
              slotDatetime="2026-08-20T11:00:00"
              ticketCount={1}
              venueName="Versova, Mumbai"
              googleMapsUrl="https://maps.google.com/?q=Versova,Mumbai"
            />
          </YStack>

          {/* Long location, short title — the location row truncates to ONE
            line with an ellipsis (numberOfLines={1} on that PSText.Body2)
            so a full street address can't push the card wide; the title
            column above it has room to spare. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              long location — truncates to one line, short title leaves room to
              spare
            </Text>
            <ReservationListItem
              imageUrl="https://picsum.photos/id/1043/600/800"
              title="Brunch"
              slotDatetime="2026-08-18T12:30:00"
              ticketCount={2}
              venueName="The Rooftop Terrace, 4th Floor, Prestige Atlanta, 80 Feet Main Road, Koramangala 1st Block, Bengaluru, Karnataka 560034"
              googleMapsUrl="https://maps.google.com/?q=Prestige+Atlanta+Koramangala"
            />
          </YStack>

          {/* Long title — clamps at 3 lines (PSText.Body1 numberOfLines={3}
            on the component); the image column stretches to match whatever
            height that 3-line block ends up needing (items="stretch" on the
            row), not the other way round. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              long title — clamps to 3 lines; the image stretches to match the
              text column&apos;s height
            </Text>
            <ReservationListItem
              imageUrl="https://picsum.photos/id/1025/600/800"
              title="The Grand Midsummer Rooftop Vinyl Sessions: An Eight-Night Residency of Deep-Listening Sets, Natural Wine & Skyline Views Above Marine Drive"
              slotDatetime="2026-09-05T20:00:00"
              ticketCount={4}
              venueName="Koramangala, Bangalore"
              googleMapsUrl="https://maps.google.com/?q=Koramangala,Bangalore"
            />
          </YStack>

          {/* No imageUrl — `event.images[0]?.url` (or `.at(0)?.url`) is
            genuinely undefined whenever an event has no photos uploaded;
            not a synthetic edge case. The component passes `uri: undefined`
            straight through to expo-image's `source`, which just renders
            nothing in the image box rather than a broken-image glyph — the
            title/date/location column is unaffected. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              no imageUrl — event has no uploaded photos; image box renders
              empty, rest of the row is unaffected
            </Text>
            <ReservationListItem
              title="Sunset Kayaking"
              slotDatetime="2026-08-22T17:30:00"
              ticketCount={3}
              venueName="Mahim Creek, Mumbai"
              googleMapsUrl="https://maps.google.com/?q=Mahim+Creek,Mumbai"
            />
          </YStack>

          {/* past — dims the photo (+ grayscale via MaybeGrayscale) and the
            text/location sections, each opacity'd SEPARATELY rather than on
            the outer row, per the component's own docstring. One thing the
            docstring claims that the CURRENT code doesn't do: a "Finished"
            PSBadge floating over the image's bottom-left, kept full-opacity
            against the dimmed photo. `PSBadge` is imported in
            TicketListItem.tsx but never actually rendered anywhere in the
            component body — so this preview shows the real, badge-less
            output, not the stale docstring's. profile's "Past bookings"
            section is where this state ships. */}
          <YStack alignSelf="stretch" gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              past — photo grayscaled + dimmed, text dimmed separately; NO
              &quot;Finished&quot; badge despite the component&apos;s docstring
              — that render never actually happens in the current code
            </Text>
            <ReservationListItem
              imageUrl="https://picsum.photos/id/1035/600/800"
              title="An Evening of Sufi Qawwali & Slow-Cooked Awadhi Dinner on the Lawn"
              slotDatetime="2025-12-14T19:30:00"
              ticketCount={1}
              venueName="The Terrace, Bandra"
              googleMapsUrl="https://maps.google.com/?q=The+Terrace,Bandra"
              onPress={() => setTapped((t) => !t)}
              past
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
